'use client';

import { useEffect, useRef } from 'react';

interface Particle3D {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  size: number;
  opacity: number;
  type: 'leaf' | 'water' | 'carbon' | 'gold' | 'hex';
  rotation: number;
  rotationSpeed: number;
  color: string;
  glowColor: string;
}

export default function MotionBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle3D[]>([]);
  const animationFrameRef = useRef<number>();
  const timeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize 3D particles
    const initParticles = () => {
      const particles: Particle3D[] = [];
      const configs = [
        { type: 'leaf' as const, color: '#10b981', glowColor: 'rgba(16,185,129,0.4)', count: 12 },
        { type: 'water' as const, color: '#3b82f6', glowColor: 'rgba(59,130,246,0.4)', count: 10 },
        { type: 'carbon' as const, color: '#6b7280', glowColor: 'rgba(107,114,128,0.3)', count: 8 },
        { type: 'gold' as const, color: '#fbbf24', glowColor: 'rgba(251,191,36,0.5)', count: 10 },
        { type: 'hex' as const, color: '#a78bfa', glowColor: 'rgba(167,139,250,0.4)', count: 8 },
      ];

      configs.forEach(({ type, color, glowColor, count }) => {
        for (let i = 0; i < count; i++) {
          particles.push({
            x: Math.random() * (canvas?.width || 1200),
            y: Math.random() * (canvas?.height || 800),
            z: Math.random() * 600 + 100,
            vx: (Math.random() - 0.5) * 0.6,
            vy: (Math.random() - 0.5) * 0.4,
            vz: (Math.random() - 0.5) * 0.3,
            size: Math.random() * 10 + 5,
            opacity: Math.random() * 0.5 + 0.15,
            type,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.025,
            color,
            glowColor,
          });
        }
      });

      particlesRef.current = particles;
    };

    initParticles();

    // Project 3D to 2D with perspective
    const project3D = (x: number, y: number, z: number, w: number, h: number) => {
      const fov = 600;
      const scale = fov / (fov + z);
      return {
        px: x * scale + w / 2 * (1 - scale),
        py: y * scale + h / 2 * (1 - scale),
        scale,
      };
    };

    // Draw 3D particle with glow
    const drawParticle3D = (p: Particle3D, w: number, h: number) => {
      const { px, py, scale } = project3D(p.x - w / 2, p.y - h / 2, p.z, w, h);
      const drawX = px + w / 2;
      const drawY = py + h / 2;
      const drawSize = p.size * scale;
      const drawOpacity = p.opacity * scale;

      ctx.save();
      ctx.translate(drawX, drawY);
      ctx.rotate(p.rotation);
      ctx.globalAlpha = drawOpacity;

      // Glow effect
      ctx.shadowBlur = 15 * scale;
      ctx.shadowColor = p.glowColor;

      switch (p.type) {
        case 'leaf': {
          // 3D-looking leaf with gradient
          const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, drawSize * 1.5);
          grad.addColorStop(0, '#6ee7b7');
          grad.addColorStop(0.5, p.color);
          grad.addColorStop(1, '#065f46');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.ellipse(0, 0, drawSize * 0.7, drawSize * 1.4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Vein
          ctx.strokeStyle = 'rgba(255,255,255,0.4)';
          ctx.lineWidth = 0.8 * scale;
          ctx.beginPath();
          ctx.moveTo(0, -drawSize * 1.2);
          ctx.lineTo(0, drawSize * 1.2);
          ctx.stroke();
          break;
        }
        case 'water': {
          // 3D sphere-like water drop
          const wGrad = ctx.createRadialGradient(-drawSize * 0.3, -drawSize * 0.3, 0, 0, 0, drawSize);
          wGrad.addColorStop(0, '#bfdbfe');
          wGrad.addColorStop(0.4, p.color);
          wGrad.addColorStop(1, '#1e3a8a');
          ctx.fillStyle = wGrad;
          ctx.beginPath();
          ctx.arc(0, 0, drawSize, 0, Math.PI * 2);
          ctx.fill();
          // Highlight
          ctx.fillStyle = 'rgba(255,255,255,0.6)';
          ctx.beginPath();
          ctx.ellipse(-drawSize * 0.25, -drawSize * 0.25, drawSize * 0.25, drawSize * 0.15, -0.5, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case 'carbon': {
          // 3D hexagonal carbon molecule
          ctx.fillStyle = p.color;
          ctx.strokeStyle = '#9ca3af';
          ctx.lineWidth = 1 * scale;
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            const hx = Math.cos(angle) * drawSize;
            const hy = Math.sin(angle) * drawSize;
            if (i === 0) ctx.moveTo(hx, hy);
            else ctx.lineTo(hx, hy);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          // Inner glow
          const cGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, drawSize);
          cGrad.addColorStop(0, 'rgba(156,163,175,0.5)');
          cGrad.addColorStop(1, 'transparent');
          ctx.fillStyle = cGrad;
          ctx.fill();
          break;
        }
        case 'gold': {
          // 3D star/sparkle
          ctx.fillStyle = p.color;
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 0.5 * scale;
          ctx.beginPath();
          for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI) / 4;
            const r = i % 2 === 0 ? drawSize : drawSize * 0.45;
            const sx = Math.cos(angle) * r;
            const sy = Math.sin(angle) * r;
            if (i === 0) ctx.moveTo(sx, sy);
            else ctx.lineTo(sx, sy);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          // Shimmer
          const gGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, drawSize);
          gGrad.addColorStop(0, 'rgba(255,255,255,0.8)');
          gGrad.addColorStop(0.3, 'rgba(251,191,36,0.4)');
          gGrad.addColorStop(1, 'transparent');
          ctx.fillStyle = gGrad;
          ctx.fill();
          break;
        }
        case 'hex': {
          // Glowing hexagon (data/tech feel)
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1.5 * scale;
          ctx.fillStyle = 'transparent';
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3 + Math.PI / 6;
            const hx = Math.cos(angle) * drawSize;
            const hy = Math.sin(angle) * drawSize;
            if (i === 0) ctx.moveTo(hx, hy);
            else ctx.lineTo(hx, hy);
          }
          ctx.closePath();
          ctx.stroke();
          // Inner hex
          ctx.strokeStyle = 'rgba(167,139,250,0.3)';
          ctx.lineWidth = 0.8 * scale;
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3 + Math.PI / 6;
            const hx = Math.cos(angle) * drawSize * 0.6;
            const hy = Math.sin(angle) * drawSize * 0.6;
            if (i === 0) ctx.moveTo(hx, hy);
            else ctx.lineTo(hx, hy);
          }
          ctx.closePath();
          ctx.stroke();
          break;
        }
      }

      ctx.restore();
    };

    // Draw connecting lines between nearby particles
    const drawConnections = (w: number, h: number) => {
      const particles = particlesRef.current;
      ctx.save();
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 200) {
            const { px: x1, py: y1 } = project3D(particles[i].x - w / 2, particles[i].y - h / 2, particles[i].z, w, h);
            const { px: x2, py: y2 } = project3D(particles[j].x - w / 2, particles[j].y - h / 2, particles[j].z, w, h);
            const alpha = (1 - dist / 200) * 0.08;
            ctx.strokeStyle = `rgba(16,185,129,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(x1 + w / 2, y1 + h / 2);
            ctx.lineTo(x2 + w / 2, y2 + h / 2);
            ctx.stroke();
          }
        }
      }
      ctx.restore();
    };

    // Animation loop
    const animate = () => {
      const w = canvas.width;
      const h = canvas.height;
      timeRef.current += 0.008;

      ctx.clearRect(0, 0, w, h);

      // Draw connections first
      drawConnections(w, h);

      // Sort by z for proper depth rendering
      const sorted = [...particlesRef.current].sort((a, b) => b.z - a.z);

      sorted.forEach((particle) => {
        // Update 3D position
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.z += particle.vz + Math.sin(timeRef.current + particle.x * 0.01) * 0.2;
        particle.rotation += particle.rotationSpeed;

        // Gentle floating wave
        particle.y += Math.sin(timeRef.current * 0.5 + particle.x * 0.005) * 0.15;

        // Boundary wrapping
        if (particle.x < -50) particle.x = w + 50;
        if (particle.x > w + 50) particle.x = -50;
        if (particle.y < -50) particle.y = h + 50;
        if (particle.y > h + 50) particle.y = -50;
        if (particle.z < 50) particle.z = 700;
        if (particle.z > 700) particle.z = 50;

        drawParticle3D(particle, w, h);
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.55 }}
    />
  );
}